/* import __COLOCATED_TEMPLATE__ from './multi-select.hbs'; */
import $ from 'jquery';
import RSVP from 'rsvp';
import Component from '@ember/component';
import { equal } from '@ember/object/computed';
import { scheduleOnce, bind } from '@ember/runloop';
import { isEmpty } from '@ember/utils';
import { observer, computed } from '@ember/object';
import { task, timeout } from 'ember-concurrency';

export default Component.extend({
  classNames: ['multi-select'],
  options: [],
  selectedOptions: [],
  listedOptions: [],
  textValue: '',
  dropdownIsShowing: false,
  shouldTriggerFocus: true,
  isLoading: false,
  readonly: null,
  limit: null,
  /**
   * for non unique entity's, we want to display the name as well as the id for easier lookup.
   *
   * Passed in from caller
   * @type {boolean}
   */
  combineWithId: false,
  /**
   * for non unique entity's, we use the entity id for later lookup.
   *
   * Passed in from caller
   * @type {Number}
   */
  entityId: null,

  /**
   * Set to true to disable the form input while data is being preloaded.
   *
   * Passed in from caller
   * @type {boolean}
   */
  isDisableWhilePreLoading: false,
  isSingleSelect: equal('limit', 1),
  showAvatar: undefined,
  // action to call when pressing enter if not selecting from dropdown
  // used if you need to override form submission for some reason
  enterAction: undefined, // action to be called when pressing enter u
  pattern: computed('search', function () {
    return this.get('search') ? "[\\w\\s]{3,}" : null;
  }),


  /**
   * Used internally to tell input whether or not to disable itself.
   *
   * @type {boolean}
   */
  isDisabled: false,

  /**
   * Will used internally as a mutex for the preloading operation.
   *
   * @type {boolean}
   */
  isPreloading: false,

  actions: {
    removeSelected(option) {
      this._removeFromSelected(option, this.selectedOptions);
    }
  },

  /**
   *
   */
  didInsertElement() {
    this._super(...arguments);
    let search = this.get('textValue');
    this.get('_search').perform(search);
    if (this.get('preload')) {
      scheduleOnce('afterRender', this, () => {
        this.get('_preload').perform().then(() => {
          this.get('_filter').perform('', true);
        });
      });
    }
    scheduleOnce('afterRender', this, 'readonlyChanged');
    scheduleOnce('afterRender', this, '_setSearchDelay');
    scheduleOnce('afterRender', this, '_setupMouseOver');
    scheduleOnce('afterRender', this, function() {
      $(this.element).on('keydown', this.get('handleKeyDown').bind(this));
    });
  },

  /*************************************
   * Observers
   ************************************/

  /**
   *
   */
  readonlyChanged: observer('readonly', function () {
    let readonly = this.get('readonly');
    if (readonly && !isEmpty(this.get('selectedOptions'))) {
      this.set('textValue', ' ');
    } else if (!readonly && !isEmpty(this.get('selectedOptions'))) {
      this.set('textValue', '');
    }
  }),

  /**
   *
   */
  valueChanged: observer('textValue', function () {
    let search = this.get('textValue');
    if (!this.get('readonly')) {
      //reset entity id when text is empty
      if (isEmpty(search)){
        this.set('entityId', null);
      }
      this.get('_search').perform(search).then(() => {
        if (!isEmpty(this.get('listedOptions')) && !isEmpty(search)) {
          this.showDropdown();
        } else {
          this.hideDropdown();
        }
      }).catch(() => {
      });
    }
  }),

  /*************************************
   * Search Functions
   ************************************/

  //
  searchFilter: undefined, //[optional]
  _defaultSearchFilter: {
    name: null
  },

  // Time in ms to wait after user stops typing
  searchDelay: undefined, //[Optional]
  _preloadedSearchDelay: 100,
  _defaultSearchDelay: 300,

  /**
   *
   * @param search
   * @returns {RSVP.Promise}
   * @optional
   */
  search: undefined,

  /**
   *
   * @param search
   * @param context
   * @returns {RSVP.Promise}
   * @private
   */
  _defaultSearch(search, context) {
    return new RSVP.Promise(function (resolve) {
      resolve(context.get('options'), search);
    });
  },

  /**
   *
   */
  _preload: task(function *() {
    let filter = this.get('searchFilter') ?? null;
    this.set('isPreloading', true);
    this.set('isDisabled', this.get('isDisableWhilePreLoading'));

    let modelPromise = yield this.get('preload')(filter).then(models => {
      this.set('options', []);
      models.forEach(model => {
        this.get('options').pushObject(model);
      });
      return models;
    });
    this.set('isPreloading', false);
    this.set('isDisabled', false);
    return modelPromise;
  }).drop(),

  /**
   *
   */
  _search: task(function *(searchStr) {
    let ms = this.get('searchDelay');
    yield timeout(ms);
    searchStr = searchStr.trim();
    if (!this.get('search') || searchStr.length >= 3) {
      this.set('isLoading', true);
      let func = this.get('search') ?? this.get('_defaultSearch');
      let searchFilter = this._parseSearchFilter(searchStr);
      yield func(searchFilter, this).then(models => {
        this.set('options', []);
        models.forEach(model => {
          this.get('options').pushObject(model);
        });
        this.get('_filter').perform(searchStr);
      });
    }
  }).restartable(),

  _defaultFilterFieldName: 'name',
  filterFieldName: undefined,
  /**
   * After _search is done providing all options, filter out the ones for listedOptions
   */
  _filter: task(function *(search, force) {
    search = search.trim();
    let modifiedSearch = isEmpty(search) ? '.' : search.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&");
    modifiedSearch = modifiedSearch.replace(/\s/g, '.*');
    const fieldName = this.filterFieldName ?? this._defaultFilterFieldName;
    const options = (this.options ?? []).filter((option) => {
      const regex = new RegExp(modifiedSearch, 'gi');
      const propVal = option[fieldName];
      return (!isEmpty(modifiedSearch) || force) && option && propVal && propVal.match(regex);
    });
    options.forEach((item) => {
      item.set('name', item[fieldName]);
    });
    this.create?.(options, search);
    this.set('listedOptions', options);
    this.set('isLoading', false);
    yield timeout();
  }).restartable(),

  /*************************************
   * Internal Functions
   ************************************/

  /**
   *
   * @param option
   * @param {Ember.MutableArray} selectedOptions
   * @param limit
   * @private
   */
  _addToSelected(option, selectedOptions, limit) {
    if (option) {
      let alreadySelected = selectedOptions.any((item) => {
        return item.get('id') === option.get('id');
      });
      if (!alreadySelected) {
        if (limit === 1) {
          selectedOptions.removeObject(selectedOptions.get('firstObject'));
        }
        selectedOptions.pushObject(option);
      }
    }
  },

  /**
   *
   * @param option
   * @param {Number} limit
   * @private
   */
  _afterSelect(option, limit) {
    if (option) {
      const str = limit === 1 ? option.name : '';
      this.set('textValue', str);
      if (option.id && typeof option.id !== "undefined"){
        this.set('entityId', option.id);
      }
      this.set('shouldTriggerFocus', false);
      $(this.element).find('input').focus();
      this.hideDropdown();
      this.afterSelected?.(option);
    }
  },

  /**
   *
   * @param {string} searchStr
   * @returns {{}}
   * @private
   */
  _parseSearchFilter(searchStr) {
    let filter = this.get('searchFilter') ?? this.get('_defaultSearchFilter');
    let tmpFilter = {};
    for (let key in filter) {
      if (!filter.hasOwnProperty(key)) {
        continue;
      }
      tmpFilter[key] = (filter[key] === null) ? searchStr : filter[key];
    }
    return tmpFilter;
  },

  /**
   *
   * @param option
   * @param {array} selectOptions
   * @private
   */
  _removeFromSelected(option, selectOptions) {
    selectOptions.removeObject(option);
  },

  /**
   *
   * @private
   */
  _setSearchDelay() {
    if (this.get('searchDelay')) {
      return;
    }
    let delay = this.get('search') ? this.get('_defaultSearchDelay') : this.get('_preloadedSearchDelay');
    this.set('searchDelay', delay);
  },

  /*************************************
   * Dropdown Menu
   ************************************/

  dropdownIndex: -1,
  dropdownIndexChanged(focus) {
    let $dropdown = $(this.element).find('.ms-dropdown');
    $dropdown.find('.active').toggleClass('active');
    let $target = $dropdown.find('li').eq(this.get('dropdownIndex'));
    $target.addClass('active');
    if (focus) {
      $(this.element).find('.ms-dropdown')[0].scrollTop = $target.position().top;
    }
  },

  /**
   *
   */
  hideDropdown() {
    this.set('dropdownIndex', -1);
    this.set('dropdownIsShowing', false);
  },

  /**
   *
   */
  showDropdown() {
    this.set('dropdownIndex', -1);
    if (!isEmpty(this.get('listedOptions'))) {
      this.set('dropdownIsShowing', true);
    }
  },

  /**
   *
   * @private
   */
  _reduceDropdownIndex() {
    let index = this.get('dropdownIndex');
    if (index > 0) {
      this.set('dropdownIndex', index - 1);
    } else {
      this.set('dropdownIndex', this.get('listedOptions').length - 1);
    }
  },

  /**
   *
   * @private
   */
  _increaseDropdownIndex() {
    let index = this.get('dropdownIndex');
    if (index < (this.get('listedOptions').length - 1)) {
      this.set('dropdownIndex', index + 1);
    } else {
      this.set('dropdownIndex', 0);
    }
  },

  /*************************************
   * User Interactions
   ************************************/

  /**
   *
   * @param {jQuery.Event} e
   */
  handleKeyDown(e) {
    switch (e.keyCode) {
      case 13: //Enter
        if (this.get('dropdownIsShowing')) {
          e.preventDefault();
          let index = this.get('dropdownIndex');
          // execute action if there is no selection
          let enterAction = this.get('enterAction');
          if ((index === -1) && enterAction) {
            enterAction();
          }
          let option = this.get('listedOptions')[index];
          let limit = this.get('limit');
          this._addToSelected(option, this.get('selectedOptions'), limit);
          this._afterSelect(option, limit);
        } else {
          let enterAction = this.get('enterAction');
          if (enterAction) {
            enterAction();
          }
        }
        break;
      case 27: //Esc
        this.hideDropdown();
        break;
      case 38: //Up
        if (this.get('dropdownIsShowing')) {
          e.preventDefault();
          this._reduceDropdownIndex();
          let dropdownIndexChanged = bind(this, this.dropdownIndexChanged);
          dropdownIndexChanged(true);
        }
        break;
      case 40: //Down
        if (this.get('dropdownIsShowing')) {
          e.preventDefault();
          this._increaseDropdownIndex();
          let dropdownIndexChanged = bind(this, this.dropdownIndexChanged);
          dropdownIndexChanged(true);
        } else {
          this.showDropdown();
        }
        break;
    }
  },

  /**
   *
   * @param {jQuery.Event} e
   */
  click(e) {
    let $target = $(e.target);
    if ($target.hasClass('collection-item')) {
      e.preventDefault();
      let index = $(e.target).index();
      let option = this.get('listedOptions')[index];
      let limit = this.get('limit');
      this._addToSelected(option, this.get('selectedOptions'), limit);
      this._afterSelect(option, limit);
    } else if ($target.hasClass('ember-text-field') && this.get('listedOptions') && !$target.attr('readonly')) {
      if (!this.get('dropdownIsShowing')) {
        this.showDropdown();
      }
    }
  },

  _mouseover(e) {
    let self = e.data.self;
    self.set('dropdownIndex', $(e.target).index());
    let func = bind(self, self.dropdownIndexChanged);
    func();
  },
  _setupMouseOver() {
    $(this.element).on('mouseover', '.collection-item', {self: this}, this._mouseover);
    // $(this.element).off('mouseover', '.collection-item', {self: this}, this._select);
  },

  onBlur: task(function *(self) {
    yield timeout(200);
    self.set('dropdownIsShowing', false);
  }),

  onFocus: task(function *() {
    yield timeout(200);
    if (this.get('shouldTriggerFocus') && !this.get('dropdownIsShowing')) {
      this.showDropdown();
    }
    this.set('shouldTriggerFocus', true);
  })
});
